<template>
  <div data-tour-id="samples-register-template-samples-grid">
    <DxDataGrid
      :data-source="preparedSamples"
      key-expr="rowKey"
      :show-borders="false"
      :show-column-lines="false"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :column-auto-width="true"
      :column-min-width="130"
      :word-wrap-enabled="true"
      :show-column-headers="true"
      :show-colon="false"
      :remote-operations="false"
      :load-panel="{ enabled: false }"
      :hover-state-enabled="false"
      :show-row-lines="false"
      :row-alternation-enabled="false"
      :no-data-text="$t('noDataText')"
      class="custom-grid custom-template-samples"
      @content-ready="init"
      @editing-start="onEditingStart"
      @toolbar-preparing="onToolbarPreparing($event)"
      @row-updated="onRowUpdated"
    >
      <DxScrolling mode="standard" />
      <DxEditing
        mode="row"
        :allow-updating="canEditRows"
        :allow-adding="false"
        :allow-deleting="false"
        :use-icons="true"
      />

      <DxPaging :page-size="10" />

      <DxPager
        :show-page-size-selector="true"
        :allowed-page-sizes="[5, 10, 20]"
        :show-info="true"
        info-text="{2} items"
        :visible="true"
      />

      <DxSearchPanel
        :visible="true"
        :width="300"
        :placeholder="$t('search')"
      />

      <DxColumn
        data-field="order"
        :caption="$t('sample')"
        alignment="center"
        :width="90"
        :allow-editing="false"
        :allow-header-filtering="false"
      />

      <DxColumn
        v-if="showBudgetColumn"
        data-field="sampleBudgetCode"
        :caption="$t('samplesRegister.budget')"
        :width="210"
        :calculate-cell-value="getSampleBudgetLabelValue"
        edit-cell-template="budgetEditor"
        :allow-editing="canEditRows"
        :allow-header-filtering="false"
        alignment="center"
      />

      <template #budgetEditor="{ data: cell }">
        <DxSelectBox
          :value="cell.data.sampleBudgetCode"
          :data-source="normalizedBudgetOptions"
          :display-expr="getBudgetOptionText"
          value-expr="CodPresupuesto"
          :search-enabled="true"
          :disabled="!canEditRows || loadingEditorData"
          @value-changed="(e) => onRowBudgetChanged(e.value, cell)"
        />
      </template>

      <DxColumn
        data-field="descripcionMuestra"
        :caption="$t('samplesRegister.sampleDescription')"
        :width="130"
        css-class="wrap-long-header"
        :allow-editing="canEditRows"
        :allow-header-filtering="false"
        alignment="center"
      />

      <DxColumn
        data-field="familiaTMId"
        :caption="$t('samplesRegister.family')"
        :min-width="160"
        :calculate-cell-value="getFamilyDescription"
        edit-cell-template="familyEditor"
        :allow-editing="canEditRows"
        :allow-header-filtering="false"
        alignment="center"
      />

      <template #familyEditor="{ data: cell }">
        <DxSelectBox
          :value="cell.data.familiaTMId"
          :data-source="familiesFiltered"
          display-expr="FamiliaTM"
          value-expr="idFamiliaTM"
          :search-enabled="true"
          :disabled="!canEditRows || loadingEditorData"
          @value-changed="(e) => onRowFamilyChanged(e.value, cell)"
        />
      </template>

      <DxColumn
        data-field="tipoMuestraId"
        :caption="$t('samplesRegister.sampleType')"
        :width="130"
        :calculate-cell-value="getSampleTypeDescription"
        edit-cell-template="sampleTypeEditor"
        :allow-editing="canEditRows"
        :allow-header-filtering="false"
        alignment="center"
      />

      <template #sampleTypeEditor="{ data: cell }">
        <DxSelectBox
          :value="cell.data.tipoMuestraId"
          :data-source="getRowSampleTypes(cell.data)"
          display-expr="Descripcion"
          value-expr="idTipoMuestra"
          :search-enabled="true"
          :disabled="
            !canEditRows ||
            getRowUiProp(cell.data, 'sampleTypesDisabled')
          "
          @value-changed="
            (e) => onRowSampleTypeChanged(e.value, cell)
          "
        />
      </template>

      <DxColumn
        data-field="subTipoMuestraId"
        :caption="$t('samplesRegister.sampleSubtype')"
        :width="130"
        css-class="wrap-long-header"
        :calculate-cell-value="getSubTypeDescription"
        edit-cell-template="subTypeEditor"
        :allow-editing="canEditRows"
        :allow-header-filtering="false"
        alignment="center"
      />

      <template #subTypeEditor="{ data: cell }">
        <DxSelectBox
          :value="cell.data.subTipoMuestraId"
          :data-source="
            getRowUiProp(cell.data, 'subSampleType') || []
          "
          display-expr="Descripcion"
          value-expr="idSubTipoMuestra"
          :search-enabled="true"
          :disabled="
            !canEditRows ||
            getRowUiProp(cell.data, 'subSampleTypeVisible')
          "
          @value-changed="(e) => onRowSubTypeChanged(e.value, cell)"
        />
      </template>

      <DxColumn
        data-field="articuloId"
        :caption="$t('samplesRegister.analysisType')"
        :min-width="300"
        :calculate-cell-value="getAnalysisDescription"
        edit-cell-template="analysisEditor"
        :allow-editing="canEditRows"
        :allow-header-filtering="false"
        alignment="center"
      />

      <template #analysisEditor="{ data: cell }">
        <DxSelectBox
          :value="cell.data.articuloId"
          :data-source="getRowArticlesBySubFamily(cell.data)"
          display-expr="AtDescrip"
          value-expr="CodArticulo"
          :search-enabled="true"
          :disabled="
            !canEditRows ||
            getRowUiProp(cell.data, 'analysisTypeDisabled')
          "
          @value-changed="(e) => onRowAnalysisChanged(e.value, cell)"
        />
      </template>

      <DxColumn
        v-if="editable"
        data-field="automaticSamplingPoint"
        :caption="$t('samplesRegister.automaticSamplingPoint')"
        :min-width="180"
        alignment="center"
        cell-template="cellTemplateBoolean"
        edit-cell-template="automaticSamplingPointEditor"
        :allow-editing="canEditRows"
        :allow-header-filtering="false"
      />

      <template #automaticSamplingPointEditor="{ data: cell }">
        <DxCheckBox
          :value="toBoolean(cell.data.automaticSamplingPoint)"
          :disabled="
            !canEditRows || !isSamplingPointSectionVisible(cell.data)
          "
          @value-changed="
            (e) => onRowAutomaticSamplingPointChanged(e.value, cell)
          "
        />
      </template>

      <DxColumn
        data-field="fincaValue"
        :caption="$t('samplesRegister.farm')"
        :min-width="220"
        :calculate-cell-value="getFarmDescription"
        edit-cell-template="farmEditor"
        :allow-editing="canEditRows"
        :allow-header-filtering="false"
        alignment="center"
      />

      <template #farmEditor="{ data: cell }">
        <div v-if="!isSamplingPointSectionVisible(cell.data)">
          {{ $t('notApplicable') }}
        </div>
        <DxSelectBox
          v-else-if="getAutomaticSamplingPointValue(cell.data)"
          :value="cell.data.fincaId"
          :data-source="farmsWithParcels"
          display-expr="Finca"
          value-expr="idFinca"
          :search-enabled="true"
          :disabled="!canEditRows"
          @value-changed="(e) => onRowFarmChanged(e.value, cell)"
        />
        <DxTextBox
          v-else
          :value="cell.data.fincaLiteral"
          :disabled="!canEditRows"
          @value-changed="
            (e) => onRowFarmLiteralChanged(e.value, cell)
          "
        />
      </template>

      <DxColumn
        data-field="parcelaValue"
        :caption="$t('samplesRegister.plot')"
        :min-width="220"
        :calculate-cell-value="getPlotDescription"
        edit-cell-template="plotEditor"
        :allow-editing="canEditRows"
        :allow-header-filtering="false"
        alignment="center"
      />

      <template #plotEditor="{ data: cell }">
        <div v-if="!isSamplingPointSectionVisible(cell.data)">
          {{ $t('notApplicable') }}
        </div>
        <DxSelectBox
          v-else-if="getAutomaticSamplingPointValue(cell.data)"
          :value="cell.data.parcelaId"
          :data-source="getRowParcels(cell.data)"
          display-expr="Parcela"
          value-expr="idParcela"
          :search-enabled="true"
          :disabled="
            !canEditRows || !getResolvedRowData(cell.data).fincaId
          "
          @value-changed="(e) => onRowPlotChanged(e.value, cell)"
        />
        <DxTextBox
          v-else
          :value="cell.data.parcelaLiteral"
          :disabled="!canEditRows"
          @value-changed="
            (e) => onRowPlotLiteralChanged(e.value, cell)
          "
        />
      </template>

      <DxColumn
        data-field="nivelPuntoValue"
        :caption="$t('samplesRegister.pointCoordinate')"
        :min-width="220"
        :calculate-cell-value="getPointDescription"
        edit-cell-template="pointEditor"
        :allow-editing="canEditRows"
        :allow-header-filtering="false"
        alignment="center"
      />

      <template #pointEditor="{ data: cell }">
        <div v-if="!isSamplingPointSectionVisible(cell.data)">
          {{ $t('notApplicable') }}
        </div>
        <DxSelectBox
          v-else-if="getAutomaticSamplingPointValue(cell.data)"
          :value="cell.data.nivelPuntoId"
          :data-source="getRowLevelPoints(cell.data)"
          :display-expr="getLevelPointOptionText"
          value-expr="idNivelPunto"
          :search-enabled="true"
          :disabled="
            !canEditRows || !getResolvedRowData(cell.data).parcelaId
          "
          @value-changed="
            (e) => onRowLevelPointChanged(e.value, cell)
          "
        />
        <DxTextBox
          v-else
          :value="cell.data.nivelPuntoLiteral"
          :disabled="
            !canEditRows || !getAutomaticSamplingPointValue(cell.data)
          "
          @value-changed="
            (e) => onRowLevelPointLiteralChanged(e.value, cell)
          "
        />
      </template>

      <DxColumn
        data-field="observaciones"
        :caption="$t('samplesRegister.sampleObservations')"
        :width="180"
        css-class="wrap-long-header"
        :allow-editing="canEditRows"
        :allow-header-filtering="false"
        alignment="center"
      />

      <DxColumn
        data-field="legislacionId"
        :caption="$t('samplesRegister.legislation')"
        :min-width="180"
        :calculate-cell-value="getLegislationDescription"
        edit-cell-template="legislationEditor"
        :allow-editing="canEditRows"
        :allow-header-filtering="false"
        alignment="center"
      />

      <template #legislationEditor="{ data: cell }">
        <DxSelectBox
          :value="cell.data.legislacionId"
          :data-source="legislations"
          display-expr="Legislacion"
          value-expr="idLegislacion"
          :search-enabled="true"
          :disabled="
            !canEditRows ||
            !getRowUiProp(cell.data, 'legislationVisible')
          "
          @value-changed="
            (e) => onRowLegislationChanged(e.value, cell)
          "
        />
      </template>

      <DxColumn
        data-field="cma"
        :caption="$t('samplesRegister.cmaEnabled')"
        :width="110"
        alignment="center"
        cell-template="cellTemplateBoolean"
        edit-cell-template="cmaEditor"
        :allow-editing="canEditRows"
        :allow-header-filtering="false"
      />

      <template #cmaEditor="{ data: cell }">
        <DxCheckBox
          :value="toBoolean(cell.data.cma)"
          :disabled="
            !canEditRows || !getRowUiProp(cell.data, 'checkCma')
          "
          @value-changed="
            (e) => onRowBooleanChanged('cma', e.value, cell)
          "
        />
      </template>

      <DxColumn
        data-field="lmr"
        :caption="$t('samplesRegister.lmrEnabled')"
        :width="110"
        alignment="center"
        cell-template="cellTemplateBoolean"
        edit-cell-template="lmrEditor"
        :allow-editing="canEditRows"
        :allow-header-filtering="false"
      />

      <template #lmrEditor="{ data: cell }">
        <DxCheckBox
          :value="toBoolean(cell.data.lmr)"
          :disabled="
            !canEditRows || !getRowUiProp(cell.data, 'checkLmr')
          "
          @value-changed="
            (e) => onRowBooleanChanged('lmr', e.value, cell)
          "
        />
      </template>

      <DxColumn
        data-field="trazaCultivo"
        :caption="$t('samplesRegister.trace')"
        :width="130"
        alignment="center"
        cell-template="cellTemplateBoolean"
        edit-cell-template="traceEditor"
        :allow-editing="canEditRows"
        :allow-header-filtering="false"
      />

      <template #traceEditor="{ data: cell }">
        <DxCheckBox
          :value="toBoolean(cell.data.trazaCultivo)"
          :disabled="
            !canEditRows ||
            !getRowUiProp(cell.data, 'traceCultiveVisible')
          "
          @value-changed="
            (e) => onRowBooleanChanged('trazaCultivo', e.value, cell)
          "
        />
      </template>

      <DxColumn
        data-field="cultivoOrganico"
        :caption="$t('samplesRegister.organicFarming')"
        :width="160"
        alignment="center"
        cell-template="cellTemplateBoolean"
        edit-cell-template="organicEditor"
        :allow-editing="canEditRows"
        :allow-header-filtering="false"
      />

      <template #organicEditor="{ data: cell }">
        <DxCheckBox
          :value="toBoolean(cell.data.cultivoOrganico)"
          :disabled="
            !canEditRows ||
            !getRowUiProp(cell.data, 'traceCultiveVisible')
          "
          @value-changed="
            (e) =>
              onRowBooleanChanged('cultivoOrganico', e.value, cell)
          "
        />
      </template>

      <DxColumn
        v-if="editable"
        type="buttons"
        :buttons="editButtons"
        :fixed="true"
        fixed-position="right"
        alignment="center"
        :width="110"
      />

      <template #cellTemplateBoolean="{ data }">
        <span :class="getBooleanClass(data.value)">
          {{ formatBoolean(data.value) }}
        </span>
      </template>
    </DxDataGrid>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import {
  DxDataGrid,
  DxColumn,
  DxSearchPanel,
  DxPager,
  DxPaging,
  DxScrolling,
  DxEditing,
} from 'devextreme-vue/data-grid';
import DxSelectBox from 'devextreme-vue/select-box';
import DxTextBox from 'devextreme-vue/text-box';
import DxCheckBox from 'devextreme-vue/check-box';
import { NOTIFY_TIME_LONG } from '@/js/constants';

export default {
  name: 'TemplateSamplesDetails',
  components: {
    DxDataGrid,
    DxColumn,
    DxSearchPanel,
    DxPager,
    DxPaging,
    DxScrolling,
    DxEditing,
    DxSelectBox,
    DxTextBox,
    DxCheckBox,
  },
  props: {
    samples: {
      type: Array,
      default: () => [],
    },
    editable: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    budgetCode: {
      type: [String, Number],
      default: null,
    },
    showBudgetColumn: {
      type: Boolean,
      default: false,
    },
    budgetOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      grid: null,
      preparedSamples: [],
      loadingEditorData: false,
      editorDataReady: false,
      familiesFiltered: [],
      analysisType: [],
      offerLines: [],
      sampleTypesByFamily: {},
      editorDataVersion: 0,
      analysisBudgetKey: '',
      editorDataLoadPromise: null,
      offerLinesBudgetKey: '',
      editorDataBudgetKey: '',
      activeBudgetCode: null,
    };
  },
  computed: {
    ...mapState('samplesRegisterModule', [
      'sampletypes',
      'families',
      'legislations',
      'farmsWithParcels',
    ]),
    ...mapState('Offers', [
      'presupuestos',
      'detallesPresupuestos',
      'analisisTipoDescrip',
    ]),
    canEditRows() {
      return this.editable && !this.saving;
    },
    editButtons() {
      return [
        {
          name: 'edit',
          cssClass: 'samples-register-template-samples-edit-button',
        },
        {
          name: 'save',
        },
        {
          name: 'cancel',
        },
      ];
    },
    yesText() {
      return this.$t('yes') !== 'yes' ? this.$t('yes') : 'Si';
    },
    noText() {
      return this.$t('no') !== 'no' ? this.$t('no') : 'No';
    },
    normalizedBudgetOptions() {
      const budgets = Array.isArray(this.budgetOptions)
        && this.budgetOptions.length
        ? this.budgetOptions
        : this.presupuestos;

      return (Array.isArray(budgets) ? budgets : []).map((item) => ({
        ...item,
        CodPresupuesto:
          this.normalizeTemplateText(item?.CodPresupuesto) || null,
      }));
    },
  },
  watch: {
    samples: {
      handler(value) {
        this.preparedSamples = this.buildEditableSamples(value);
        if (this.editable && this.editorDataReady) {
          this.hydratePreparedRows();
        }
      },
      immediate: true,
      deep: true,
    },
    editable(value) {
      if (value) {
        this.initializeEditorData();
      }
    },
    async budgetCode(newValue, oldValue) {
      if (!this.editable) return;
      if (String(newValue || '') === String(oldValue || '')) return;
      this.activeBudgetCode = null;
      await this.initializeEditorData({ force: true });
    },
    saving(value) {
      if (!this.grid) return;

      if (value) {
        this.grid.cancelEditData?.();
        return;
      }

      this.$nextTick(() => this.refreshGrid());
    },
  },
  mounted() {
    if (this.editable) {
      this.initializeEditorData();
    }
    this.loadBudgets();
  },
  methods: {
    getTranslatedSubFamilyLabel(line) {
      const familyId = Number(line?.IdFamilia);
      const subFamilyId = Number(
        line?.IdSubFamilia ?? line?.IdSubFamiliaTM
      );
      const family = (this.families || []).find(
        (entry) => Number(entry?.idFamiliaTM) === familyId
      );
      const subFamily = family?.SubFamiliasTM?.find(
        (entry) => Number(entry?.idSubFamiliaTM) === subFamilyId
      );

      return subFamily?.SubFamiliaTM ?? line?.SubFamiliaTM ?? '';
    },
    async loadBudgets() {
      try {
        await this.fetchPresupuestos();
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(
                error?.response || error?.message || error
              )
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      }
    },
    normalizeSelectionId(value) {
      if (value === null || value === undefined || value === '') {
        return null;
      }
      const normalizedValue = Number(value);
      return Number.isNaN(normalizedValue) ? value : normalizedValue;
    },
    normalizeTemplateText(value) {
      if (value === null || value === undefined) return '';
      return String(value).trim();
    },
    normalizeText(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();
    },
    toBoolean(value) {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'number') return value === 1;
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        return (
          normalized === 'true' ||
          normalized === '1' ||
          normalized === 'si' ||
          normalized === 'yes'
        );
      }
      return false;
    },
    getBudgetCodeValue() {
      return (
        this.normalizeTemplateText(this.activeBudgetCode) ||
        this.normalizeTemplateText(this.budgetCode) ||
        null
      );
    },
    getSampleBudgetObject(sample = {}) {
      return (
        sample?.codPresupuesto ||
        sample?.CodPresupuesto ||
        sample?._originalSample?.codPresupuesto ||
        sample?._originalSample?.CodPresupuesto ||
        null
      );
    },
    getSampleBudgetCode(sample = {}) {
      const budget = this.getSampleBudgetObject(sample);
      if (!budget) return null;

      if (typeof budget === 'object') {
        return (
          this.normalizeTemplateText(
            budget?.id ||
              budget?.codigo ||
              budget?.codPresupuesto ||
              budget?.CodPresupuesto
          ) || null
        );
      }

      return this.normalizeTemplateText(budget) || null;
    },
    getSampleBudgetDescription(sample = {}) {
      const budget = this.getSampleBudgetObject(sample);
      if (!budget || typeof budget !== 'object') return '';

      return (
        this.normalizeTemplateText(
          budget?.descripcion || budget?.Descripcion
        ) || ''
      );
    },
    formatSampleBudgetLabel(sample = {}) {
      const budgetCode = this.getSampleBudgetCode(sample);
      const budgetDescription =
        this.getSampleBudgetDescription(sample);

      if (!budgetCode) return '';
      if (!budgetDescription) return budgetCode;

      const normalizedCode = String(budgetCode).trim().toLowerCase();
      const normalizedDescription = String(budgetDescription)
        .trim()
        .toLowerCase();

      if (
        normalizedDescription === normalizedCode ||
        normalizedDescription.startsWith(`${normalizedCode} -`)
      ) {
        return budgetDescription;
      }

      return `${budgetCode} - ${budgetDescription}`;
    },
    getSampleBudgetLabelValue(rowData) {
      return this.formatSampleBudgetLabel(
        this.getResolvedRowData(rowData)
      );
    },
    getBudgetOptionText(option) {
      const budgetCode =
        this.normalizeTemplateText(option?.CodPresupuesto) || '';
      const title = this.normalizeTemplateText(option?.Titulo) || '';
      const status =
        option?.CodEstado === '1'
          ? this.normalizeTemplateText(
              this.$t(this.$helpers.getStatusTextKey(option?.Estado))
            )
          : '';

      if (option?.CodOfferTitle) {
        return option.CodOfferTitle;
      }

      return `${budgetCode}${title ? ` - ${title}` : ''}${
        status ? ` (${status})` : ''
      }`;
    },
    getBudgetOptionByCode(budgetCode) {
      if (!budgetCode) return null;

      return (
        this.normalizedBudgetOptions.find(
          (item) =>
            String(item?.CodPresupuesto || '') ===
            String(budgetCode || '')
        ) || null
      );
    },
    buildDefaultRowUi(familyId) {
      return {
        sampleTypesDisabled: true,
        subSampleTypeVisible: true,
        analysisTypeDisabled: true,
        traceCultiveVisible: false,
        legislationVisible: false,
        checkCma: false,
        checkLmr: false,
        subSampleType: [],
        idSubFamiliaTiposMuestras: null,
        filteredParcels: [],
        filteredLevelPoints: [],
        sampleTypesInternal: [],
        showSamplingPoints: Number(familyId) !== 20,
      };
    },
    buildEditableSamples(samples = []) {
      return (samples || []).map((sample, index) => {
        const familyId = this.normalizeSelectionId(
          sample?.familiaTM?.id ??
            sample?.familiaTM?.idFamiliaTM ??
            sample?.FamiliaTM?.id ??
            sample?.FamiliaTM?.idFamiliaTM ??
            sample?.idFamilia ??
            sample?.IdFamilia ??
            sample?.FamiliaTM
        );
        const sampleTypeId = this.normalizeSelectionId(
          sample?.tipoMuestra?.id ??
            sample?.idTipoMuestra ??
            sample?.IdTipoMuestra ??
            sample?.Descripcion?.idTipoMuestra ??
            sample?.Descripcion
        );
        const subTypeId = this.normalizeSelectionId(
          sample?.subTipoMuestra?.id ??
            sample?.idSubTipoMuestra ??
            sample?.IdSubTipoMuestra ??
            sample?.SubTipoMuestra
        );
        const legislationId = this.normalizeSelectionId(
          sample?.legislacion?.id ??
            sample?.idLegislacion ??
            sample?.IdLegislacion ??
            sample?.Legislacion
        );
        const farmId = this.normalizeSelectionId(
          sample?.finca?.id ?? sample?.Parcelas ?? sample?.idFinca
        );
        const plotId = this.normalizeSelectionId(
          sample?.parcela?.id ?? sample?.Parcela ?? sample?.idParcela
        );
        const pointId = this.normalizeSelectionId(
          sample?.nivelPunto?.id ??
            sample?.idNivelPunto ??
            sample?.IdNivelPunto
        );
        const automaticSamplingPoint =
          sample?.automaticSamplingPoint === null ||
          sample?.automaticSamplingPoint === undefined
            ? true
            : this.toBoolean(sample?.automaticSamplingPoint);
        const fincaLiteral =
          this.normalizeTemplateText(
            sample?.fincaLiteral ??
              sample?.FincaLiteral ??
              sample?.finca?.descripcion
          ) || '';
        const parcelaLiteral =
          this.normalizeTemplateText(
            sample?.parcelaLiteral ??
              sample?.ParcelaLiteral ??
              sample?.parcela?.descripcion
          ) || '';
        const nivelPuntoLiteral =
          this.normalizeTemplateText(
            sample?.nivelPuntoLiteral ??
              sample?.NivelPuntoCoordLiteral ??
              sample?.nivelPunto?.descripcion
          ) || '';
        const articuloId =
          this.normalizeTemplateText(
            sample?.articulo?.id ??
              sample?.CodArticulo ??
              sample?.codArticulo ??
              sample?.Articulo
          ) || null;

        return {
          ...sample,
          originalSample: {
            ...sample,
          },
          rowKey:
            sample?.id ||
            sample?.IdWMuestra ||
            `${sample?.order || index + 1}-${index}`,
          order: sample?.order || index + 1,
          sampleBudgetCode: this.getSampleBudgetCode(sample),
          sampleBudgetLabel: this.formatSampleBudgetLabel(sample),
          descripcionMuestra:
            this.normalizeTemplateText(
              sample?.descripcionMuestra ?? sample?.DescripcionMuestra
            ) || '',
          observaciones:
            this.normalizeTemplateText(
              sample?.observaciones ?? sample?.Observaciones
            ) || '',
          automaticSamplingPoint,
          familiaTMId: familyId,
          familiaTMDescripcion:
            this.normalizeTemplateText(
              sample?.familiaTM?.descripcion
            ) || '',
          tipoMuestraId: sampleTypeId,
          tipoMuestraDescripcion:
            this.normalizeTemplateText(
              sample?.tipoMuestra?.descripcion
            ) || '',
          subTipoMuestraId: subTypeId,
          subTipoMuestraDescripcion:
            this.normalizeTemplateText(
              sample?.subTipoMuestra?.descripcion
            ) || '',
          articuloId,
          articuloDescripcion:
            this.normalizeTemplateText(
              sample?.articulo?.descripcion
            ) || '',
          legislacionId: legislationId,
          legislacionDescripcion:
            this.normalizeTemplateText(
              sample?.legislacion?.descripcion
            ) || '',
          fincaId: farmId,
          parcelaId: plotId,
          nivelPuntoId: pointId,
          fincaLiteral,
          parcelaLiteral,
          nivelPuntoLiteral,
          fincaValue: automaticSamplingPoint ? farmId : fincaLiteral,
          parcelaValue: automaticSamplingPoint
            ? plotId
            : parcelaLiteral,
          nivelPuntoValue: automaticSamplingPoint
            ? pointId
            : nivelPuntoLiteral,
          cma: this.toBoolean(sample?.cma),
          lmr: this.toBoolean(sample?.lmr),
          trazaCultivo: this.toBoolean(sample?.trazaCultivo),
          cultivoOrganico: this.toBoolean(
            sample?.cultivoOrganico ?? sample?.CultivoOrganico
          ),
          ui: this.buildDefaultRowUi(familyId),
        };
      });
    },
    rebuildFamiliesFiltered() {
      const sourceFamilies = Array.isArray(this.families)
        ? this.families
        : [];
      if (!this.offerLines.length) {
        this.familiesFiltered = this.getBudgetCodeValue()
          ? []
          : [...sourceFamilies];
        return;
      }

      const availableFamilyIds = new Set(
        this.offerLines
          .map((item) => this.normalizeSelectionId(item?.IdFamilia))
          .filter((value) => value !== null)
      );

      this.familiesFiltered = sourceFamilies.filter((family) =>
        availableFamilyIds.has(
          this.normalizeSelectionId(family?.idFamiliaTM)
        )
      );
    },
    getFamilyById(idFamilia) {
      const normalizedId = this.normalizeSelectionId(idFamilia);
      if (normalizedId === null) return null;
      return (this.familiesFiltered || []).find(
        (item) =>
          this.normalizeSelectionId(item?.idFamiliaTM) ===
          normalizedId
      );
    },
    isFamilyAvailableForCurrentBudget(idFamilia) {
      const normalizedId = this.normalizeSelectionId(idFamilia);
      if (normalizedId === null) return false;
      return (this.familiesFiltered || []).some(
        (item) =>
          this.normalizeSelectionId(item?.idFamiliaTM) ===
          normalizedId
      );
    },
    clearRowSelectionsForBudgetChange(rowData) {
      const row = this.getResolvedRowData(rowData);
      if (!row?.rowKey) return;

      this.updateRowFields(row, {
        familiaTMId: null,
        IdFamilia: null,
        familiaTMDescripcion: null,
        tipoMuestraId: null,
        IdTipoMuestra: null,
        tipoMuestraDescripcion: null,
        subTipoMuestraId: null,
        IdSubTipoMuestra: null,
        subTipoMuestraDescripcion: null,
        articuloId: null,
        articuloDescripcion: null,
        legislacionId: null,
        IdLegislacion: null,
        legislacionDescripcion: null,
        cma: false,
        lmr: false,
        trazaCultivo: false,
        cultivoOrganico: false,
        CodLinea: null,
        idSubFamiliaTM: null,
        IdSubFamiliaTM: null,
        PrecioAT: null,
        Descuento: null,
      });
      this.updateRowUi(row, this.buildDefaultRowUi(null));
    },
    normalizeSampleTypes(sampleTypes = []) {
      return (sampleTypes || []).map((item) => ({
        ...item,
        idTipoMuestra: this.normalizeSelectionId(item.idTipoMuestra),
        idSubFamiliaTM: this.normalizeSelectionId(
          item.idSubFamiliaTM ?? item.id_sub_familia
        ),
        id_sub_familia: this.normalizeSelectionId(
          item.id_sub_familia ?? item.idSubFamiliaTM
        ),
        SubTiposMuestras: this.normalizeSubSampleTypes(
          item.SubTiposMuestras
        ),
      }));
    },
    normalizeSubSampleTypes(subTypes = []) {
      return (subTypes || []).map((item) => ({
        ...item,
        idSubTipoMuestra: this.normalizeSelectionId(
          item.idSubTipoMuestra
        ),
      }));
    },
    async initializeEditorData({ force = false } = {}) {
      if (!this.editable) return;
      if (this.editorDataLoadPromise) {
        await this.editorDataLoadPromise;
        const activeBudgetKey =
          this.getBudgetCodeValue() || '__NO_BUDGET__';
        if (force || this.editorDataBudgetKey !== activeBudgetKey) {
          await this.initializeEditorData({ force: true });
        }
        return;
      }

      const budgetKey = this.getBudgetCodeValue() || '__NO_BUDGET__';
      const budgetCode = this.getBudgetCodeValue();
      const shouldReloadBudgetData =
        force || this.offerLinesBudgetKey !== budgetKey;
      const familiesLoaded =
        Array.isArray(this.families) && this.families.length > 0;
      const farmsLoaded =
        Array.isArray(this.farmsWithParcels) &&
        this.farmsWithParcels.length > 0;

      if (
        !force &&
        this.editorDataReady &&
        this.editorDataBudgetKey === budgetKey &&
        familiesLoaded &&
        farmsLoaded
      ) {
        return;
      }

      const version = this.editorDataVersion + 1;
      this.editorDataVersion = version;
      this.loadingEditorData = true;
      this.editorDataReady = false;

      if (shouldReloadBudgetData) {
        this.sampleTypesByFamily = {};
        this.analysisBudgetKey = '';
      }

      this.editorDataLoadPromise = (async () => {
        try {
          const baseRequests = [];
          if (!familiesLoaded) {
            baseRequests.push(this.fetchFamiliesByHierarchy());
          }
          if (!farmsLoaded) {
            baseRequests.push(this.fetchFarmsAndParcels());
          }
          if (baseRequests.length) {
            await Promise.all(baseRequests);
          }

          if (shouldReloadBudgetData) {
            if (budgetCode) {
              await this.fetchDetallesPresupuestos(budgetCode);
              this.offerLines = Array.isArray(
                this.detallesPresupuestos
              )
                ? [...this.detallesPresupuestos]
                : [];
            } else {
              this.offerLines = [];
            }
            this.offerLinesBudgetKey = budgetKey;
          }

          this.rebuildFamiliesFiltered();
          await this.hydratePreparedRows();

          if (version === this.editorDataVersion) {
            this.editorDataReady = true;
            this.editorDataBudgetKey = budgetKey;
          }
        } catch (error) {
          this.notifyError(error);
        } finally {
          if (version === this.editorDataVersion) {
            this.loadingEditorData = false;
          }
          this.editorDataLoadPromise = null;
        }
      })();

      await this.editorDataLoadPromise;
    },
    async ensureEditorDataReady() {
      if (this.editorDataLoadPromise) {
        await this.editorDataLoadPromise;
        return;
      }
      await this.initializeEditorData();
    },
    async ensureAnalysisTypeLoaded(idFamilia, idSubFamilia) {
      const normalizedFamilyId = this.normalizeSelectionId(idFamilia);
      const normalizedSubFamilyId =
        this.normalizeSelectionId(idSubFamilia);

      if (
        normalizedFamilyId === null ||
        normalizedSubFamilyId === null
      ) {
        this.analysisType = [];
        return [];
      }

      const matchingLines = (this.offerLines || []).filter(
        (line) =>
          this.normalizeSelectionId(line?.IdFamilia) ===
            normalizedFamilyId &&
          this.normalizeSelectionId(
            line?.IdSubFamilia ?? line?.IdSubFamiliaTM
          ) === normalizedSubFamilyId
      );

      if (!matchingLines.length) {
        this.analysisType = [];
        return [];
      }

      this.analysisType = await this.analysisTypeDescrForLine(
        matchingLines
      );
      this.analysisBudgetKey = `${
        this.getBudgetCodeValue() || '__NO_BUDGET__'
      }|${normalizedFamilyId}|${normalizedSubFamilyId}`;
      return this.analysisType;
    },
    async analysisTypeDescrForLine(offerLines = []) {
      const [referenceLine] = offerLines || [];

      if (!referenceLine) {
        return [];
      }

      await this.fetchAnalisisTipo({
        codPlanta: referenceLine.CodPlantaCentroProduccionSeleccionada,
        idFamiliaTM: referenceLine.IdFamilia,
        idSubFamiliaTM:
          referenceLine.IdSubFamilia ?? referenceLine.IdSubFamiliaTM,
        codCliente: null,
        codIdioma: null,
      });

      const analysisTypeResponse = this.analisisTipoDescrip || [];

      return [...offerLines].map((line) => {
        let selectedAnalysis = null;
        if (
          this.normalizeSelectionId(line?.IdSubFamiliaTM) === 63 ||
          this.normalizeSelectionId(line?.IdSubFamilia) === 63
        ) {
          selectedAnalysis = analysisTypeResponse.find(
            (item) => item.AnalisisTipo === line.Articulo
          );
        } else {
          const analysisTypeId = String(line.CodArticulo || '').slice(
            1
          );
          selectedAnalysis = analysisTypeResponse.find(
            (item) =>
              item.IdAnalisisTipo?.toString() === analysisTypeId
          );
        }

        const subFamiliaDescripcion =
          this.getTranslatedSubFamilyLabel(line);
        const analysisDescription =
          selectedAnalysis?.DescripcionAT ??
          line?.DescripcionAT ??
          '';

        return {
          ...line,
          CodAnalisisTipo:
            selectedAnalysis?.CodAnalisisTipo ?? line?.CodAnalisisTipo,
          DescripcionAT:
            selectedAnalysis?.IdSubFamiliaTM === 63
              ? selectedAnalysis?.CodAnalisisTipo
              : analysisDescription,
          AtDescrip: `${line.Articulo} - ${analysisDescription} - ${subFamiliaDescripcion}`,
        };
      });
    },
    async loadSampleTypesForFamily(idFamilia) {
      const normalizedFamilyId = this.normalizeSelectionId(idFamilia);
      if (normalizedFamilyId === null) {
        return {
          loadedTypes: [],
          subFamilies: [],
        };
      }

      const cacheKey = String(normalizedFamilyId);
      if (this.sampleTypesByFamily[cacheKey]) {
        return this.sampleTypesByFamily[cacheKey];
      }
      const subFamilies = [
        ...new Set(
          (this.offerLines || [])
            .filter(
              (line) =>
                this.normalizeSelectionId(line?.IdFamilia) ===
                normalizedFamilyId
            )
            .map((line) =>
              this.normalizeSelectionId(
                line?.IdSubFamilia ?? line?.IdSubFamiliaTM
              )
            )
            .filter((value) => value !== null)
        ),
      ];
      await this.fetchSamplesTypes({
        IdFamilia: normalizedFamilyId,
        subfamilias: subFamilies,
      });

      const loadedTypes = this.normalizeSampleTypes(this.sampletypes);
      const result = {
        loadedTypes,
        subFamilies,
      };
      this.$set(this.sampleTypesByFamily, cacheKey, result);
      return result;
    },
    getRowFromStore(rowOrKey) {
      const key =
        typeof rowOrKey === 'string'
          ? rowOrKey
          : rowOrKey?.rowKey || rowOrKey?.id;
      if (!key) return null;

      return (
        this.preparedSamples.find((row) => row.rowKey === key) ||
        null
      );
    },
    getResolvedRowData(rowData) {
      return this.getRowFromStore(rowData) || rowData || {};
    },
    getRowUiProp(rowData, propName) {
      const resolvedRow = this.getResolvedRowData(rowData);
      return resolvedRow?.ui?.[propName];
    },
    updateRowFields(rowOrKey, fields) {
      const row = this.getResolvedRowData(rowOrKey);
      if (!row?.rowKey) return;

      const rowIndex = this.preparedSamples.findIndex(
        (item) => item.rowKey === row.rowKey
      );

      Object.entries(fields).forEach(([key, value]) => {
        this.$set(row, key, value);
        if (rowIndex !== -1) {
          this.$set(this.preparedSamples[rowIndex], key, value);
        }
      });
    },
    updateRowUi(rowOrKey, properties) {
      const row = this.getResolvedRowData(rowOrKey);
      if (!row?.rowKey) return;

      const currentUi = {
        ...(row.ui || {}),
        ...properties,
      };
      this.updateRowFields(row, {
        ui: currentUi,
      });
    },
    async onRowBudgetChanged(value, cell) {
      const row = this.getResolvedRowData(cell.data);
      const nextBudgetCode =
        this.normalizeTemplateText(value) || null;
      const currentBudgetCode =
        this.normalizeTemplateText(row?.sampleBudgetCode) || null;

      if (
        String(nextBudgetCode || '') ===
        String(currentBudgetCode || '')
      ) {
        return;
      }

      const selectedBudget = this.getBudgetOptionByCode(nextBudgetCode);
      const budgetDescription =
        selectedBudget?.CodOfferTitle ||
        this.getSampleBudgetDescription(row) ||
        nextBudgetCode;

      this.$f7?.preloader?.show?.();

      try {
        this.activeBudgetCode = nextBudgetCode;
        await this.initializeEditorData({ force: true });
        this.clearRowSelectionsForBudgetChange(row);
        this.updateRowFields(row, {
          sampleBudgetCode: nextBudgetCode,
          sampleBudgetLabel: selectedBudget
            ? this.getBudgetOptionText(selectedBudget)
            : nextBudgetCode || '',
          codPresupuesto: nextBudgetCode
            ? {
                id: nextBudgetCode,
                descripcion: budgetDescription,
              }
            : null,
          CodPresupuesto: nextBudgetCode
            ? {
                id: nextBudgetCode,
                descripcion: budgetDescription,
              }
            : null,
        });
        this.refreshRow(cell);
      } finally {
        this.$f7?.preloader?.hide?.();
      }
    },
    refreshRow(cell) {
      const gridInstance = this.grid;
      if (!gridInstance) return;

      const rowIndex =
        typeof cell?.rowIndex === 'number'
          ? cell.rowIndex
          : typeof cell?.row?.rowIndex === 'number'
          ? cell.row.rowIndex
          : null;

      if (
        rowIndex !== null &&
        typeof gridInstance.repaintRows === 'function'
      ) {
        gridInstance.repaintRows([rowIndex]);
        return;
      }

      gridInstance.refresh();
    },
    refreshGrid() {
      this.grid?.refresh?.();
    },
    findFarmById(idFinca) {
      const normalizedFarmId = this.normalizeSelectionId(idFinca);
      if (normalizedFarmId === null) return null;
      return (this.farmsWithParcels || []).find(
        (farm) =>
          this.normalizeSelectionId(farm?.idFinca) ===
          normalizedFarmId
      );
    },
    findParcelById(rowData, idParcela) {
      const normalizedPlotId = this.normalizeSelectionId(idParcela);
      if (normalizedPlotId === null) return null;
      return this.getRowParcels(rowData).find(
        (parcel) =>
          this.normalizeSelectionId(parcel?.idParcela) ===
          normalizedPlotId
      );
    },
    getFamilyDescriptionValue(rowData) {
      const row = this.getResolvedRowData(rowData);
      if (row?.familiaTMDescripcion) return row.familiaTMDescripcion;

      const family = this.getFamilyById(row?.familiaTMId);
      return (
        family?.FamiliaTM ||
        row?.familiaTM?.descripcion ||
        this.normalizeTemplateText(row?.FamiliaTM) ||
        ''
      );
    },
    getSampleTypeDescriptionValue(rowData) {
      const row = this.getResolvedRowData(rowData);
      if (row?.tipoMuestraDescripcion)
        return row.tipoMuestraDescripcion;

      const sampleTypes = this.getRowSampleTypes(row);
      const selectedSampleType = sampleTypes.find(
        (item) =>
          this.normalizeSelectionId(item?.idTipoMuestra) ===
          this.normalizeSelectionId(row?.tipoMuestraId)
      );

      return (
        selectedSampleType?.Descripcion ||
        row?.tipoMuestra?.descripcion ||
        ''
      );
    },
    getSubTypeDescriptionValue(rowData) {
      const row = this.getResolvedRowData(rowData);
      if (row?.subTipoMuestraDescripcion) {
        return row.subTipoMuestraDescripcion;
      }

      const subTypes = this.getRowUiProp(row, 'subSampleType') || [];
      const selectedSubType = subTypes.find(
        (item) =>
          this.normalizeSelectionId(item?.idSubTipoMuestra) ===
          this.normalizeSelectionId(row?.subTipoMuestraId)
      );

      return (
        selectedSubType?.Descripcion ||
        row?.subTipoMuestra?.descripcion ||
        ''
      );
    },
    getAnalysisDescriptionValue(rowData) {
      const row = this.getResolvedRowData(rowData);
      if (row?.articuloDescripcion) return row.articuloDescripcion;

      const selectedArticle = this.analysisType.find(
        (item) =>
          String(item?.CodArticulo) === String(row?.articuloId)
      );

      return (
        selectedArticle?.AtDescrip ||
        selectedArticle?.Articulo ||
        row?.articulo?.descripcion ||
        ''
      );
    },
    getLegislationDescriptionValue(rowData) {
      const row = this.getResolvedRowData(rowData);
      if (row?.legislacionDescripcion)
        return row.legislacionDescripcion;

      const selectedLegislation = (this.legislations || []).find(
        (item) =>
          this.normalizeSelectionId(item?.idLegislacion) ===
          this.normalizeSelectionId(row?.legislacionId)
      );

      return (
        selectedLegislation?.Legislacion ||
        row?.legislacion?.descripcion ||
        ''
      );
    },
    getFarmDescriptionValue(rowData) {
      const row = this.getResolvedRowData(rowData);
      if (!this.isSamplingPointSectionVisible(row)) {
        return this.$t('notApplicable');
      }

      if (!this.getAutomaticSamplingPointValue(row)) {
        return row?.fincaLiteral || '-';
      }

      const selectedFarm = this.findFarmById(row?.fincaId);
      return (
        selectedFarm?.Finca ||
        row?.finca?.descripcion ||
        row?.fincaLiteral ||
        '-'
      );
    },
    getPlotDescriptionValue(rowData) {
      const row = this.getResolvedRowData(rowData);
      if (!this.isSamplingPointSectionVisible(row)) {
        return this.$t('notApplicable');
      }

      if (!this.getAutomaticSamplingPointValue(row)) {
        return row?.parcelaLiteral || '-';
      }

      const selectedParcel = this.findParcelById(row, row?.parcelaId);
      return (
        selectedParcel?.Parcela ||
        row?.parcela?.descripcion ||
        row?.parcelaLiteral ||
        '-'
      );
    },
    getPointDescriptionValue(rowData) {
      const row = this.getResolvedRowData(rowData);
      if (!this.isSamplingPointSectionVisible(row)) {
        return this.$t('notApplicable');
      }

      if (!this.getAutomaticSamplingPointValue(row)) {
        return row?.nivelPuntoLiteral || '-';
      }

      const selectedPoint = this.getRowLevelPoints(row).find(
        (item) =>
          this.normalizeSelectionId(item?.idNivelPunto) ===
          this.normalizeSelectionId(row?.nivelPuntoId)
      );

      if (selectedPoint) {
        return this.getLevelPointOptionText(selectedPoint);
      }

      return (
        row?.nivelPunto?.descripcion || row?.nivelPuntoLiteral || '-'
      );
    },
    getFamilyDescription(rowData) {
      return this.getFamilyDescriptionValue(rowData) || '-';
    },
    getSampleTypeDescription(rowData) {
      return this.getSampleTypeDescriptionValue(rowData) || '-';
    },
    getSubTypeDescription(rowData) {
      return this.getSubTypeDescriptionValue(rowData) || '-';
    },
    getAnalysisDescription(rowData) {
      return this.getAnalysisDescriptionValue(rowData) || '-';
    },
    getLegislationDescription(rowData) {
      return this.getLegislationDescriptionValue(rowData) || '-';
    },
    getFarmDescription(rowData) {
      return this.getFarmDescriptionValue(rowData) || '-';
    },
    getPlotDescription(rowData) {
      return this.getPlotDescriptionValue(rowData) || '-';
    },
    getPointDescription(rowData) {
      return this.getPointDescriptionValue(rowData) || '-';
    },
    getLevelPointOptionText(item) {
      if (!item) return '';
      const label = item?.NivelPunto || this.$t('point');
      return item?.GPSZ ? `${label} - ${item.GPSZ}` : label;
    },
    isSamplingPointSectionVisible(rowData) {
      return Boolean(
        this.getRowUiProp(rowData, 'showSamplingPoints') ?? true
      );
    },
    getAutomaticSamplingPointValue(rowData) {
      return this.toBoolean(
        this.getResolvedRowData(rowData).automaticSamplingPoint
      );
    },
    getRowParcels(rowData) {
      return this.getRowUiProp(rowData, 'filteredParcels') || [];
    },
    getRowLevelPoints(rowData) {
      return this.getRowUiProp(rowData, 'filteredLevelPoints') || [];
    },
    getRowSampleTypes(rowData) {
      const sampleTypes =
        this.getRowUiProp(rowData, 'sampleTypesInternal') || [];
      return [...sampleTypes].sort((a, b) =>
        String(a?.Descripcion || '').localeCompare(
          String(b?.Descripcion || '')
        )
      );
    },
    getRowArticlesBySubFamily(rowData) {
      const subFamilyId = this.normalizeSelectionId(
        this.getRowUiProp(rowData, 'idSubFamiliaTiposMuestras')
      );
      if (subFamilyId === null) return [];

      return this.analysisType.filter((item) => {
        const itemSubFamily =
          item?.IdSubFamilia != null
            ? item.IdSubFamilia
            : item?.IdSubFamiliaTM;
        return (
          this.normalizeSelectionId(itemSubFamily) === subFamilyId
        );
      });
    },
    syncRowSamplingPointState(
      rowData,
      { preserveValues = true } = {}
    ) {
      const row = this.getResolvedRowData(rowData);
      if (!row?.rowKey) return;

      if (!this.isSamplingPointSectionVisible(row)) {
        this.updateRowUi(row, {
          filteredParcels: [],
          filteredLevelPoints: [],
        });
        this.updateRowFields(row, {
          automaticSamplingPoint: false,
          fincaId: null,
          parcelaId: null,
          nivelPuntoId: null,
          fincaValue: null,
          parcelaValue: null,
          nivelPuntoValue: null,
          fincaLiteral: null,
          parcelaLiteral: null,
          nivelPuntoLiteral: null,
        });
        return;
      }

      if (!this.getAutomaticSamplingPointValue(row)) {
        this.updateRowUi(row, {
          filteredParcels: [],
          filteredLevelPoints: [],
        });
        this.updateRowFields(row, {
          fincaId: null,
          parcelaId: null,
          nivelPuntoId: null,
          fincaValue: row.fincaLiteral || '',
          parcelaValue: row.parcelaLiteral || '',
          nivelPuntoValue: row.nivelPuntoLiteral || '',
          fincaLiteral:
            this.normalizeTemplateText(row.fincaLiteral) || null,
          parcelaLiteral:
            this.normalizeTemplateText(row.parcelaLiteral) || null,
          nivelPuntoLiteral:
            this.normalizeTemplateText(row.nivelPuntoLiteral) || null,
        });
        return;
      }

      let selectedFarmId = this.normalizeSelectionId(row.fincaId);
      let selectedFarm = this.findFarmById(selectedFarmId);
      if (!selectedFarm && row.fincaLiteral) {
        selectedFarm = (this.farmsWithParcels || []).find(
          (farm) =>
            this.normalizeText(farm?.Finca) ===
            this.normalizeText(row.fincaLiteral)
        );
        selectedFarmId = this.normalizeSelectionId(
          selectedFarm?.idFinca
        );
      }

      const filteredParcels = Array.isArray(selectedFarm?.Parcelas)
        ? [...selectedFarm.Parcelas]
        : [];

      let selectedPlotId = this.normalizeSelectionId(row.parcelaId);
      if (!selectedPlotId && preserveValues && row.parcelaLiteral) {
        const matchedParcel = filteredParcels.find(
          (parcel) =>
            this.normalizeText(parcel?.Parcela) ===
            this.normalizeText(row.parcelaLiteral)
        );
        selectedPlotId = this.normalizeSelectionId(
          matchedParcel?.idParcela
        );
      }

      const selectedParcel = filteredParcels.find(
        (parcel) =>
          this.normalizeSelectionId(parcel?.idParcela) ===
          selectedPlotId
      );

      const filteredLevelPoints = Array.isArray(
        selectedParcel?.nivelPuntos
      )
        ? [...selectedParcel.nivelPuntos]
        : [];

      let selectedPointId = this.normalizeSelectionId(
        row.nivelPuntoId
      );
      if (
        !selectedPointId &&
        preserveValues &&
        row.nivelPuntoLiteral
      ) {
        const matchedPoint = filteredLevelPoints.find(
          (point) =>
            this.getLevelPointOptionText(point) ===
            row.nivelPuntoLiteral
        );
        selectedPointId = this.normalizeSelectionId(
          matchedPoint?.idNivelPunto
        );
      }

      const selectedPoint = filteredLevelPoints.find(
        (point) =>
          this.normalizeSelectionId(point?.idNivelPunto) ===
          selectedPointId
      );

      this.updateRowUi(row, {
        filteredParcels,
        filteredLevelPoints,
      });
      this.updateRowFields(row, {
        fincaId: selectedFarmId,
        fincaValue: selectedFarmId,
        fincaLiteral: selectedFarm?.Finca || row.fincaLiteral || null,
        parcelaId: selectedPlotId,
        parcelaValue: selectedPlotId,
        parcelaLiteral:
          selectedParcel?.Parcela || row.parcelaLiteral || null,
        nivelPuntoId: selectedPointId,
        nivelPuntoValue: selectedPointId,
        nivelPuntoLiteral: selectedPoint
          ? this.getLevelPointOptionText(selectedPoint)
          : row.nivelPuntoLiteral || null,
      });
    },
    async ensureLegislationsLoaded() {
      if (
        Array.isArray(this.legislations) &&
        this.legislations.length
      ) {
        return;
      }
      await this.fetchLegislations();
    },
    async applyFamilySelection(
      rowData,
      idFamilia,
      { preserveValues = false } = {}
    ) {
      const row = this.getResolvedRowData(rowData);
      const normalizedFamilyId = this.normalizeSelectionId(idFamilia);
      const selectedFamily = this.getFamilyById(normalizedFamilyId);
      const showSamplingPoints = Number(normalizedFamilyId) !== 20;

      this.updateRowFields(row, {
        familiaTMId: normalizedFamilyId,
        familiaTMDescripcion: selectedFamily?.FamiliaTM || null,
      });

      this.updateRowUi(row, {
        sampleTypesDisabled: true,
        subSampleTypeVisible: true,
        analysisTypeDisabled: true,
        traceCultiveVisible: false,
        legislationVisible: false,
        checkCma: false,
        checkLmr: false,
        subSampleType: [],
        idSubFamiliaTiposMuestras: null,
        sampleTypesInternal: [],
        showSamplingPoints,
      });

      if (!preserveValues) {
        this.updateRowFields(row, {
          tipoMuestraId: null,
          tipoMuestraDescripcion: null,
          subTipoMuestraId: null,
          subTipoMuestraDescripcion: null,
          articuloId: null,
          articuloDescripcion: null,
          legislacionId: null,
          legislacionDescripcion: null,
          cma: false,
          lmr: false,
          trazaCultivo: false,
          cultivoOrganico: false,
          automaticSamplingPoint: showSamplingPoints,
          fincaId: null,
          parcelaId: null,
          nivelPuntoId: null,
          fincaLiteral: null,
          parcelaLiteral: null,
          nivelPuntoLiteral: null,
          fincaValue: null,
          parcelaValue: null,
          nivelPuntoValue: null,
          CodLinea: null,
          idSubFamiliaTM: null,
          PrecioAT: null,
          Descuento: null,
        });
      }

      if (normalizedFamilyId !== null) {
        const { loadedTypes } = await this.loadSampleTypesForFamily(
          normalizedFamilyId
        );
        this.updateRowUi(row, {
          sampleTypesInternal: loadedTypes,
          sampleTypesDisabled: false,
        });
      }

      this.syncRowSamplingPointState(row, { preserveValues });
    },
    async applySampleTypeSelection(
      rowData,
      idTipoMuestra,
      { preserveValues = false } = {}
    ) {
      const row = this.getResolvedRowData(rowData);
      const normalizedSampleTypeId =
        this.normalizeSelectionId(idTipoMuestra);
      const sampleTypes = this.getRowSampleTypes(row);
      const selectedSampleType = sampleTypes.find(
        (item) =>
          this.normalizeSelectionId(item?.idTipoMuestra) ===
          normalizedSampleTypeId
      );

      this.updateRowFields(row, {
        tipoMuestraId: normalizedSampleTypeId,
        tipoMuestraDescripcion:
          selectedSampleType?.Descripcion || null,
      });

      if (!preserveValues) {
        this.updateRowFields(row, {
          subTipoMuestraId: null,
          subTipoMuestraDescripcion: null,
          articuloId: null,
          articuloDescripcion: null,
          legislacionId: null,
          legislacionDescripcion: null,
          cma: false,
          lmr: false,
          trazaCultivo: false,
          cultivoOrganico: false,
          CodLinea: null,
          idSubFamiliaTM: null,
          PrecioAT: null,
          Descuento: null,
        });
      }

      if (!selectedSampleType) {
        this.updateRowUi(row, {
          subSampleType: [],
          subSampleTypeVisible: true,
          idSubFamiliaTiposMuestras: null,
          analysisTypeDisabled: true,
          traceCultiveVisible: false,
          legislationVisible: false,
          checkCma: false,
          checkLmr: false,
        });
        this.analysisType = [];
        return;
      }

      const normalizedSubTypes = this.normalizeSubSampleTypes(
        selectedSampleType.SubTiposMuestras
      );
      const hasSubTypes = normalizedSubTypes.length > 0;
      const existingSubTypeId = this.normalizeSelectionId(
        row.subTipoMuestraId
      );
      const selectedSubType = normalizedSubTypes.find(
        (item) =>
          this.normalizeSelectionId(item?.idSubTipoMuestra) ===
          existingSubTypeId
      );
      this.updateRowUi(row, {
        subSampleType: normalizedSubTypes,
        subSampleTypeVisible: !hasSubTypes,
        idSubFamiliaTiposMuestras: this.normalizeSelectionId(
          selectedSampleType.idSubFamiliaTM ??
            selectedSampleType.id_sub_familia
        ),
        analysisTypeDisabled: false,
        traceCultiveVisible: false,
        legislationVisible: false,
        checkCma: false,
        checkLmr: false,
      });

      await this.ensureAnalysisTypeLoaded(
        row.familiaTMId,
        this.getRowUiProp(row, 'idSubFamiliaTiposMuestras')
      );

      if (!hasSubTypes || !selectedSubType) {
        this.updateRowFields(row, {
          subTipoMuestraId: null,
          subTipoMuestraDescripcion: null,
        });
      } else {
        this.updateRowFields(row, {
          subTipoMuestraId: existingSubTypeId,
          subTipoMuestraDescripcion: selectedSubType.Descripcion,
        });
      }
    },
    async applyAnalysisSelection(
      rowData,
      codArticulo,
      { preserveValues = false } = {}
    ) {
      const row = this.getResolvedRowData(rowData);
      const normalizedArticleCode =
        this.normalizeTemplateText(codArticulo) || null;
      const selectedArticle = this.analysisType.find(
        (item) =>
          String(item?.CodArticulo) === String(normalizedArticleCode)
      );

      this.updateRowFields(row, {
        articuloId: normalizedArticleCode,
        articuloDescripcion:
          selectedArticle?.AtDescrip ||
          selectedArticle?.Articulo ||
          null,
      });

      if (selectedArticle) {
        const precio = Number(selectedArticle?.PrecioBruto);
        const descuento = Number(selectedArticle?.Descuento || 0);
        const precioConDescuento = Number.isNaN(precio)
          ? row.PrecioAT
          : Math.round((precio - (precio * descuento) / 100) * 10) /
            10;

        this.updateRowFields(row, {
          CodLinea: selectedArticle.CodLinea || null,
          idSubFamiliaTM:
            selectedArticle.IdSubFamilia ||
            selectedArticle.IdSubFamiliaTM ||
            null,
          PrecioAT: precioConDescuento,
          Descuento:
            selectedArticle.Descuento ?? row.Descuento ?? null,
        });
      } else if (!preserveValues) {
        this.updateRowFields(row, {
          CodLinea: null,
          idSubFamiliaTM: null,
          PrecioAT: null,
          Descuento: null,
        });
      }

      const selectedCmaAnalysis = this.analysisType.find(
        (item) =>
          item?.TipoServicioCma != null &&
          String(item?.CodArticulo) === String(normalizedArticleCode)
      );
      const selectedLmrAnalysis = this.analysisType.find(
        (item) =>
          item?.TipoServicioLmr != null &&
          String(item?.CodArticulo) === String(normalizedArticleCode)
      );

      let uiPatch = {};
      switch (true) {
        case selectedCmaAnalysis !== undefined:
          this.updateRowFields(row, {
            cma: true,
            lmr: false,
            trazaCultivo: false,
            cultivoOrganico: false,
          });
          uiPatch = {
            checkCma: true,
            checkLmr: false,
            traceCultiveVisible: false,
          };
          break;
        case selectedLmrAnalysis !== undefined:
          this.updateRowFields(row, {
            cma: false,
            lmr: true,
          });
          uiPatch = {
            checkCma: false,
            checkLmr: true,
            traceCultiveVisible: true,
          };
          break;
        default:
          if (!preserveValues) {
            this.updateRowFields(row, {
              cma: false,
              lmr: false,
              trazaCultivo: false,
              cultivoOrganico: false,
            });
          }
          uiPatch = {
            checkCma: false,
            checkLmr: false,
            traceCultiveVisible: false,
          };
      }

      const legislationIdFromAnalysis =
        selectedCmaAnalysis?.TipoServicioCma?.IdLegislacion;
      const hasLegislationFromAnalysis =
        Number(legislationIdFromAnalysis) > 0;

      if (hasLegislationFromAnalysis) {
        await this.ensureLegislationsLoaded();
        const normalizedLegislationId = this.normalizeSelectionId(
          legislationIdFromAnalysis
        );
        const legislation = (this.legislations || []).find(
          (item) =>
            this.normalizeSelectionId(item?.idLegislacion) ===
            normalizedLegislationId
        );
        this.updateRowFields(row, {
          legislacionId: normalizedLegislationId,
          legislacionDescripcion:
            legislation?.Legislacion ||
            row.legislacionDescripcion ||
            null,
        });
        uiPatch.legislationVisible = true;
      } else if (!preserveValues) {
        this.updateRowFields(row, {
          legislacionId: null,
          legislacionDescripcion: null,
        });
        uiPatch.legislationVisible = false;
      } else {
        uiPatch.legislationVisible = Boolean(row.legislacionId);
      }

      this.updateRowUi(row, uiPatch);
    },
    async hydrateRow(rowData) {
      const row = this.getResolvedRowData(rowData);
      if (!row?.rowKey) return;

      const familyId = this.normalizeSelectionId(row.familiaTMId);
      if (
        familyId !== null &&
        this.getBudgetCodeValue() &&
        !this.isFamilyAvailableForCurrentBudget(familyId)
      ) {
        this.clearRowSelectionsForBudgetChange(row);
        this.syncRowSamplingPointState(row, {
          preserveValues: true,
        });
        return;
      }

      await this.applyFamilySelection(row, familyId, {
        preserveValues: true,
      });

      const sampleTypeId = this.normalizeSelectionId(
        row.tipoMuestraId
      );
      if (sampleTypeId !== null) {
        await this.applySampleTypeSelection(row, sampleTypeId, {
          preserveValues: true,
        });
      }

      const articleId =
        this.normalizeTemplateText(row.articuloId) || null;
      if (articleId) {
        await this.applyAnalysisSelection(row, articleId, {
          preserveValues: true,
        });
      }

      this.syncRowSamplingPointState(row, {
        preserveValues: true,
      });
    },
    async hydratePreparedRows() {
      if (!this.editable) return;
      for (const row of this.preparedSamples) {
        await this.hydrateRow(row);
      }
      this.$nextTick(() => this.refreshGrid());
    },
    async reconcileRowsForBudgetChange() {
      this.grid?.cancelEditData?.();
      await this.initializeEditorData({ force: true });
      await this.$nextTick();
      this.refreshGrid();
      this.$emit('samples-updated', this.normalizeSamplesForEmit());
    },
    async onRowFamilyChanged(idFamilia, cell) {
      try {
        await this.applyFamilySelection(cell.data, idFamilia, {
          preserveValues: false,
        });
        cell.setValue(this.normalizeSelectionId(idFamilia));
        this.$nextTick(() => this.refreshRow(cell));
      } catch (error) {
        this.notifyError(error);
      }
    },
    async onRowSampleTypeChanged(idTipoMuestra, cell) {
      await this.applySampleTypeSelection(cell.data, idTipoMuestra, {
        preserveValues: false,
      });
      cell.setValue(this.normalizeSelectionId(idTipoMuestra));
      this.$nextTick(() => this.refreshRow(cell));
    },
    onRowSubTypeChanged(idSubTipoMuestra, cell) {
      const row = this.getResolvedRowData(cell.data);
      const subTypeId = this.normalizeSelectionId(idSubTipoMuestra);
      const selectedSubType = (
        this.getRowUiProp(row, 'subSampleType') || []
      ).find(
        (item) =>
          this.normalizeSelectionId(item?.idSubTipoMuestra) ===
          subTypeId
      );

      this.updateRowFields(row, {
        subTipoMuestraId: subTypeId,
        subTipoMuestraDescripcion:
          selectedSubType?.Descripcion || null,
      });
      cell.setValue(subTypeId);
      this.$nextTick(() => this.refreshRow(cell));
    },
    async onRowAnalysisChanged(codArticulo, cell) {
      try {
        await this.applyAnalysisSelection(cell.data, codArticulo, {
          preserveValues: false,
        });
        cell.setValue(
          this.normalizeTemplateText(codArticulo) || null
        );
        this.$nextTick(() => this.refreshRow(cell));
      } catch (error) {
        this.notifyError(error);
      }
    },
    onRowAutomaticSamplingPointChanged(value, cell) {
      const row = this.getResolvedRowData(cell.data);
      const useAutomaticSamplingPoint = this.toBoolean(value);

      this.updateRowFields(row, {
        automaticSamplingPoint: useAutomaticSamplingPoint,
        fincaId: null,
        parcelaId: null,
        nivelPuntoId: null,
        fincaLiteral: null,
        parcelaLiteral: null,
        nivelPuntoLiteral: null,
        fincaValue: null,
        parcelaValue: null,
        nivelPuntoValue: null,
      });
      this.updateRowUi(row, {
        filteredParcels: [],
        filteredLevelPoints: [],
      });
      this.syncRowSamplingPointState(row, {
        preserveValues: false,
      });

      cell.setValue(useAutomaticSamplingPoint);
      this.$nextTick(() => this.refreshRow(cell));
    },
    onRowFarmChanged(idFinca, cell) {
      const row = this.getResolvedRowData(cell.data);
      const farmId = this.normalizeSelectionId(idFinca);
      const selectedFarm = this.findFarmById(farmId);

      this.updateRowFields(row, {
        fincaId: farmId,
        fincaLiteral: selectedFarm?.Finca || null,
        fincaValue: farmId,
        parcelaId: null,
        parcelaLiteral: null,
        parcelaValue: null,
        nivelPuntoId: null,
        nivelPuntoLiteral: null,
        nivelPuntoValue: null,
      });
      this.updateRowUi(row, {
        filteredParcels: selectedFarm?.Parcelas
          ? [...selectedFarm.Parcelas]
          : [],
        filteredLevelPoints: [],
      });
      cell.setValue(farmId);
      this.$nextTick(() => this.refreshRow(cell));
    },
    onRowFarmLiteralChanged(value, cell) {
      const row = this.getResolvedRowData(cell.data);
      const farmLiteral = this.normalizeTemplateText(value) || '';
      this.updateRowFields(row, {
        fincaLiteral: farmLiteral,
        fincaValue: farmLiteral,
      });
      cell.setValue(farmLiteral);
      this.$nextTick(() => this.refreshRow(cell));
    },
    onRowPlotChanged(idParcela, cell) {
      const row = this.getResolvedRowData(cell.data);
      const plotId = this.normalizeSelectionId(idParcela);
      const selectedParcel = this.findParcelById(row, plotId);
      const levelPoints = Array.isArray(selectedParcel?.nivelPuntos)
        ? [...selectedParcel.nivelPuntos]
        : [];

      this.updateRowFields(row, {
        parcelaId: plotId,
        parcelaLiteral: selectedParcel?.Parcela || null,
        parcelaValue: plotId,
        nivelPuntoId: null,
        nivelPuntoLiteral: null,
        nivelPuntoValue: null,
      });
      this.updateRowUi(row, {
        filteredLevelPoints: levelPoints,
      });
      cell.setValue(plotId);
      this.$nextTick(() => this.refreshRow(cell));
    },
    onRowPlotLiteralChanged(value, cell) {
      const row = this.getResolvedRowData(cell.data);
      const plotLiteral = this.normalizeTemplateText(value) || '';
      this.updateRowFields(row, {
        parcelaLiteral: plotLiteral,
        parcelaValue: plotLiteral,
      });
      cell.setValue(plotLiteral);
      this.$nextTick(() => this.refreshRow(cell));
    },
    onRowLevelPointChanged(idNivelPunto, cell) {
      const row = this.getResolvedRowData(cell.data);
      const pointId = this.normalizeSelectionId(idNivelPunto);
      const selectedPoint = this.getRowLevelPoints(row).find(
        (item) =>
          this.normalizeSelectionId(item?.idNivelPunto) === pointId
      );

      this.updateRowFields(row, {
        nivelPuntoId: pointId,
        nivelPuntoLiteral: selectedPoint
          ? this.getLevelPointOptionText(selectedPoint)
          : null,
        nivelPuntoValue: pointId,
      });
      cell.setValue(pointId);
      this.$nextTick(() => this.refreshRow(cell));
    },
    onRowLevelPointLiteralChanged(value, cell) {
      const row = this.getResolvedRowData(cell.data);
      const pointLiteral = this.normalizeTemplateText(value) || '';
      this.updateRowFields(row, {
        nivelPuntoLiteral: pointLiteral,
        nivelPuntoValue: pointLiteral,
      });
      cell.setValue(pointLiteral);
      this.$nextTick(() => this.refreshRow(cell));
    },
    onRowLegislationChanged(idLegislacion, cell) {
      const row = this.getResolvedRowData(cell.data);
      const legislationId = this.normalizeSelectionId(idLegislacion);
      const selectedLegislation = (this.legislations || []).find(
        (item) =>
          this.normalizeSelectionId(item?.idLegislacion) ===
          legislationId
      );

      this.updateRowFields(row, {
        legislacionId: legislationId,
        legislacionDescripcion:
          selectedLegislation?.Legislacion || null,
      });
      cell.setValue(legislationId);
      this.$nextTick(() => this.refreshRow(cell));
    },
    onRowBooleanChanged(fieldName, value, cell) {
      const row = this.getResolvedRowData(cell.data);
      const normalizedValue = this.toBoolean(value);
      this.updateRowFields(row, {
        [fieldName]: normalizedValue,
      });
      cell.setValue(normalizedValue);
      this.$nextTick(() => this.refreshRow(cell));
    },
    stripInternalSampleFields(sample) {
      const cleanedSample = {
        ...sample,
      };
      [
        'rowKey',
        'ui',
        'originalSample',
        'familiaTMId',
        'familiaTMDescripcion',
        'tipoMuestraId',
        'tipoMuestraDescripcion',
        'subTipoMuestraId',
        'subTipoMuestraDescripcion',
        'articuloId',
        'articuloDescripcion',
        'legislacionId',
        'legislacionDescripcion',
        'fincaId',
        'parcelaId',
        'nivelPuntoId',
        'fincaValue',
        'parcelaValue',
        'nivelPuntoValue',
        'nivelPuntoLiteral',
        'sampleBudgetCode',
        'sampleBudgetLabel',
      ].forEach((internalKey) => {
        delete cleanedSample[internalKey];
      });

      return cleanedSample;
    },
    buildNormalizedSample(rowData, index) {
      const row = this.getResolvedRowData(rowData);
      const automaticSamplingPoint = this.toBoolean(
        row.automaticSamplingPoint
      );
      const familyId = this.normalizeSelectionId(row.familiaTMId);
      const sampleTypeId = this.normalizeSelectionId(
        row.tipoMuestraId
      );
      const subTypeId = this.normalizeSelectionId(
        row.subTipoMuestraId
      );
      const legislationId = this.normalizeSelectionId(
        row.legislacionId
      );
      const farmId = this.normalizeSelectionId(row.fincaId);
      const plotId = this.normalizeSelectionId(row.parcelaId);
      const pointId = this.normalizeSelectionId(row.nivelPuntoId);
      const articleId =
        this.normalizeTemplateText(row.articuloId) || null;
      const hasSelectedAnalysis = Boolean(articleId);
      const familyDescription = this.getFamilyDescriptionValue(row);
      const sampleTypeDescription =
        this.getSampleTypeDescriptionValue(row);
      const subTypeDescription = this.getSubTypeDescriptionValue(row);
      const analysisDescription =
        this.getAnalysisDescriptionValue(row);
      const legislationDescription =
        this.getLegislationDescriptionValue(row);
      const farmDescription = this.getFarmDescriptionValue(row);
      const plotDescription = this.getPlotDescriptionValue(row);
      const pointDescription = this.getPointDescriptionValue(row);
      const normalizedDescription =
        this.normalizeTemplateText(row.descripcionMuestra) || '';
      const normalizedObservations =
        this.normalizeTemplateText(row.observaciones) || '';
      const analysisLineCode = hasSelectedAnalysis
        ? row.CodLinea ?? row?.originalSample?.CodLinea ?? null
        : null;
      const analysisSubFamilyId = hasSelectedAnalysis
        ? row.idSubFamiliaTM ??
          row?.originalSample?.idSubFamiliaTM ??
          row?.originalSample?.IdSubFamiliaTM ??
          null
        : null;
      const analysisPrice = hasSelectedAnalysis
        ? row.PrecioAT ?? row?.originalSample?.PrecioAT ?? null
        : null;
      const analysisDiscount = hasSelectedAnalysis
        ? row.Descuento ?? row?.originalSample?.Descuento ?? null
        : null;

      const sample = {
        ...(row.originalSample || {}),
        order: row?.order || index + 1,
        ...(row?.sampleBudgetCode
          ? {
              codPresupuesto: {
                id: row.sampleBudgetCode,
                descripcion:
                  this.getSampleBudgetDescription(row) ||
                  row.sampleBudgetCode,
              },
            }
          : {}),
        descripcionMuestra: normalizedDescription,
        DescripcionMuestra: normalizedDescription,
        observaciones: normalizedObservations,
        Observaciones: normalizedObservations,
        automaticSamplingPoint,
        familiaTM: familyId
          ? {
              id: familyId,
              descripcion: familyDescription || null,
            }
          : null,
        tipoMuestra: sampleTypeId
          ? {
              id: sampleTypeId,
              descripcion: sampleTypeDescription || null,
            }
          : null,
        subTipoMuestra: subTypeId
          ? {
              id: subTypeId,
              descripcion: subTypeDescription || null,
            }
          : null,
        articulo: articleId
          ? {
              id: articleId,
              descripcion: analysisDescription || null,
            }
          : null,
        legislacion: legislationId
          ? {
              id: legislationId,
              descripcion: legislationDescription || null,
            }
          : null,
        finca:
          automaticSamplingPoint && farmId
            ? {
                id: farmId,
                descripcion: farmDescription || null,
              }
            : null,
        parcela:
          automaticSamplingPoint && plotId
            ? {
                id: plotId,
                descripcion: plotDescription || null,
              }
            : null,
        nivelPunto:
          automaticSamplingPoint && pointId
            ? {
                id: pointId,
                descripcion: pointDescription || null,
              }
            : null,
        fincaLiteral: automaticSamplingPoint
          ? null
          : this.normalizeTemplateText(row.fincaLiteral) || null,
        parcelaLiteral: automaticSamplingPoint
          ? null
          : this.normalizeTemplateText(row.parcelaLiteral) || null,
        cma: this.toBoolean(row.cma),
        lmr: this.toBoolean(row.lmr),
        trazaCultivo: this.toBoolean(row.trazaCultivo),
        cultivoOrganico: this.toBoolean(row.cultivoOrganico),
        CultivoOrganico: this.toBoolean(row.cultivoOrganico),
        idFamilia: familyId,
        IdFamilia: familyId,
        FamiliaTM: familyId,
        idTipoMuestra: sampleTypeId,
        IdTipoMuestra: sampleTypeId,
        Descripcion: sampleTypeId
          ? {
              idTipoMuestra: sampleTypeId,
            }
          : null,
        idSubTipoMuestra: subTypeId,
        IdSubTipoMuestra: subTypeId,
        SubTipoMuestra: subTypeId,
        CodArticulo: articleId,
        codArticulo: articleId,
        Articulo: articleId,
        idLegislacion: legislationId,
        IdLegislacion: legislationId,
        Legislacion: legislationId,
        Parcelas: automaticSamplingPoint ? farmId : null,
        idFinca: automaticSamplingPoint ? farmId : null,
        Parcela: automaticSamplingPoint ? plotId : null,
        idParcela: automaticSamplingPoint ? plotId : null,
        idNivelPunto: automaticSamplingPoint ? pointId : null,
        IdNivelPunto: automaticSamplingPoint ? pointId : null,
        NivelPuntoCoordLiteral: automaticSamplingPoint
          ? pointId
            ? pointDescription || null
            : null
          : this.normalizeTemplateText(row.nivelPuntoLiteral) || null,
        CodLinea: analysisLineCode,
        idSubFamiliaTM: analysisSubFamilyId,
        IdSubFamiliaTM: analysisSubFamilyId,
        PrecioAT: analysisPrice,
        Descuento: analysisDiscount,
      };

      delete sample.CodPresupuesto;

      return this.stripInternalSampleFields(sample);
    },
    normalizeSamplesForEmit() {
      return this.preparedSamples.map((sample, index) =>
        this.buildNormalizedSample(sample, index)
      );
    },
    onRowUpdated() {
      this.grid?.cancelEditData?.();
      this.$nextTick(() => this.refreshGrid());
      this.$emit('samples-updated', this.normalizeSamplesForEmit());
    },
    async onEditingStart(e) {
      if (!this.canEditRows) return;
      const rowBudgetCode = this.getSampleBudgetCode(e?.data);
      const currentBudgetCode = this.getBudgetCodeValue();
      const shouldReloadBudgetData =
        String(rowBudgetCode || '') !== String(currentBudgetCode || '');
      if (
        this.editorDataReady &&
        !this.loadingEditorData &&
        !shouldReloadBudgetData
      ) {
        return;
      }

      e.cancel = true;
      const rowKey = e?.key;
      const gridComponent = e?.component;

      try {
        this.$f7?.preloader?.show?.();
        this.activeBudgetCode = rowBudgetCode || null;
        await this.ensureEditorDataReady();
        this.$nextTick(() => {
          const rowIndex = gridComponent?.getRowIndexByKey?.(rowKey);
          if (typeof rowIndex === 'number' && rowIndex >= 0) {
            gridComponent.editRow(rowIndex);
          }
        });
      } finally {
        this.$f7?.preloader?.hide?.();
      }
    },
    onToolbarPreparing(e) {
      const toolbarItems = e.toolbarOptions.items;
      e.toolbarOptions.elementAttr = {
        class: 'custom-simple-toolbar',
      };

      toolbarItems.forEach((item) => {
        if (item.name === 'searchPanel') {
          item.location = 'before';
        }
      });
    },
    init(e) {
      this.grid = e.component;
    },
    formatBoolean(value) {
      return value ? this.yesText : this.noText;
    },
    getBooleanClass(value) {
      return value
        ? 'template-bool template-bool--true'
        : 'template-bool template-bool--false';
    },
    notifyError(error) {
      this.$notifyDX(
        {
          message: this.$t(
            this.$helpers.getFilteredErrorMessage(
              error?.response || error?.message || error
            )
          ),
          width: 550,
        },
        'error',
        NOTIFY_TIME_LONG
      );
    },
    ...mapActions('Offers', [
      'fetchDetallesPresupuestos',
      'fetchAnalisisTipo',
      'fetchPresupuestos',
    ]),
    ...mapActions('samplesRegisterModule', [
      'fetchFamiliesByHierarchy',
      'fetchFarmsAndParcels',
      'fetchSamplesTypes',
      'fetchLegislations',
    ]),
  },
};
</script>

<style lang="scss">
@import './template-samples-details.styles.scss';
</style>
